import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, CheckCircle, AlertCircle, ChevronDown, ChevronRight, Download } from "lucide-react";
import { apiConfig } from "../config/api.config";

/* ── Helpers ── */
const fCOP = v => {
  if (v === null || v === undefined || isNaN(v) || v === "") return "—";
  return new Intl.NumberFormat("es-CO", { style:"currency", currency:"COP",
    minimumFractionDigits:0, maximumFractionDigits:0 }).format(v);
};

const MESES_NOMBRES = {
  1:"Enero",2:"Febrero",3:"Marzo",4:"Abril",5:"Mayo",6:"Junio",
  7:"Julio",8:"Agosto",9:"Septiembre",10:"Octubre",11:"Noviembre"
};

const ML = {
  auto:"a. Automático", prom:"b. Promedios", mixt:"c. Mixto",
  mc:"d. Mín. Cuadrados", inc:"e. Incr. Porcentual", prop:"f. Propio",
};

/* ── Calcular métodos con datos mensuales ── */
function calcM(item, mesesNum, pctMix) {
  const vals = mesesNum.map(m => item.recaudos?.[m] ?? null).filter(v => v !== null && v > 0);
  const n = vals.length;
  if (n === 0) return null;
  const ult = vals[vals.length - 1];
  const suma = vals.reduce((a, b) => a + b, 0);
  const prom = Math.round(suma / n);
  // Proyección anual simple: promedio mensual × 12
  const proyAnual = Math.round(prom * 12);
  // Automático: último mes × 12 × (1 + inflación por defecto 6.5%)
  const auto = Math.round(ult * 12 * 1.065);
  // Mixto: promedio mensual × 12 × (1 + ajuste)
  const mixt = Math.round(prom * 12 * (1 + pctMix / 100));
  // Mínimos cuadrados sobre los meses
  const xs = n % 2 === 1
    ? vals.map((_, i) => i - Math.floor(n / 2))
    : vals.map((_, i) => i - n / 2 + 0.5);
  const sY = suma, sXY = vals.reduce((s, y, i) => s + xs[i] * y, 0);
  const sX2 = xs.reduce((s, x) => s + x * x, 0);
  const bMC = sX2 > 0 ? sXY / sX2 : 0;
  const mcMensual = Math.round(sY / n + bMC * (xs[xs.length - 1] + 1));
  const mc = Math.round(Math.max(mcMensual, 0) * 12);
  // Incremento porcentual
  const ts = [];
  for (let i = 1; i < vals.length; i++) if (vals[i-1] > 0) ts.push((vals[i]-vals[i-1])/vals[i-1]);
  const tp = ts.length > 0 ? ts.reduce((a,b) => a+b, 0)/ts.length : 0;
  const inc = Math.round(ult * (1 + tp) * 12);
  return { auto, prom:proyAnual, mixt, mc, inc, ult, tpPct: Math.round(tp*10000)/100, sumaMeses: suma };
}

function descargar(filas, vigencia) {
  const bom = "\uFEFF";
  const cab = ["CCPET","NOMBRE","FUENTE","C.RES","TIPO","MÉTODO","VALOR PROYECTADO ANUAL","OBSERVACIONES"].join(";");
  const rows = filas.map(f => [
    f.ccpet, `"${f.nombre}"`, f.fuente??"", f.cres??"",
    f.esAgg?"Agregado":"Captura", f.met,
    String(Math.round(f.val||0)).replace(".",","),
    `"${(f.obs??"").replace(/"/g,"'")}"`,
  ].join(";"));
  const blob = new Blob([bom+cab+"\n"+rows.join("\n")],{type:"text/csv;charset=utf-8;"});
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `ProyeccionMensual_${vigencia}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Bloque({ titulo, sub, badge, open, onToggle, children }) {
  return (
    <div style={{background:"#fff",borderRadius:"16px",border:"1px solid #e5e7eb",
      boxShadow:"0 4px 14px rgba(0,0,0,0.05)",marginBottom:"1rem",overflow:"hidden"}}>
      <div onClick={onToggle} style={{padding:"1.1rem 1.5rem",cursor:"pointer",
        display:"flex",justifyContent:"space-between",alignItems:"center",
        background:open?"#eff6ff":"#fff",borderBottom:open?"1px solid #dbeafe":"none"}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:"0.75rem",flexWrap:"wrap"}}>
            <h2 style={{margin:0,color:"#163b8c",fontSize:"1rem",fontWeight:700}}>{titulo}</h2>
            {badge&&<span style={{background:"#163b8c",color:"#fff",borderRadius:"999px",
              padding:"0.15rem 0.65rem",fontSize:"0.72rem",fontWeight:700}}>{badge}</span>}
            {sub&&<span style={{fontSize:"0.8rem",color:"#6b7280"}}>{sub}</span>}
          </div>
        </div>
        <span style={{display:"flex",alignItems:"center",gap:"0.4rem",
          color:"#163b8c",fontWeight:600,fontSize:"0.85rem",marginLeft:"1rem",flexShrink:0}}>
          {open?<><ChevronDown size={16}/>Ocultar</>:<><ChevronRight size={16}/>Ver</>}
        </span>
      </div>
      {open&&<div style={{padding:"1.25rem 1.5rem"}}>{children}</div>}
    </div>
  );
}

export default function ProyeccionVigenciaActual() {
  const navigate = useNavigate();
  const fRef = useRef();

  const [datos, setDatos]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [msg, setMsg]           = useState(null);
  const [mesManual, setMesManual] = useState("");
  const [pctMix, setPctMix]     = useState(5);
  const [mets, setMets]         = useState({});
  const [propios, setPropios]   = useState({});
  const [expanded, setExpanded] = useState({});
  const [qH, setQH]             = useState("");
  const [qR, setQR]             = useState("");
  const [open, setOpen]         = useState({hist:false,liq:false,res:false});
  const tog = k => setOpen(p=>({...p,[k]:!p[k]}));

  useEffect(()=>{
    try { const s=localStorage.getItem("pva_propios"); if(s) setPropios(JSON.parse(s)); } catch{}
  },[]);
  const savePropios = np => { setPropios(np); try{localStorage.setItem("pva_propios",JSON.stringify(np));}catch{} };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(apiConfig.getUrl("/api/hacienda/proyeccion-vigencia-actual/consultar/"),{credentials:"include"});
      setDatos(await res.json());
    } catch { setDatos(null); }
    finally { setLoading(false); }
  };
  useEffect(()=>{load();},[]);

  const handleUp = async e => {
    const file = e.target.files?.[0]; if(!file) return;
    e.target.value=""; setSubiendo(true); setMsg(null);
    const fd = new FormData(); fd.append("file",file);
    if(mesManual) fd.append("mes", mesManual);
    fd.append("vigencia", new Date().getFullYear());
    try {
      const res = await fetch(apiConfig.getUrl("/api/hacienda/proyeccion-vigencia-actual/cargar/"),
        {method:"POST",body:fd,credentials:"include"});
      const d = await res.json();
      if(d.error) throw new Error(d.error);
      setMsg({ok:true,t:d.mensaje}); load();
    } catch(err){setMsg({ok:false,t:err.message});}
    finally{setSubiendo(false);}
  };

  const mesesNum   = (datos?.mesesNum ?? []).map(Number).sort((a,b)=>a-b);
  const vigencia   = datos?.vigencia ?? new Date().getFullYear();
  const nMeses     = mesesNum.length;

  const rk = it => [it.ccpet??"",it.consecutivo??"",it.cres??"",it.fuente_codigo??"",it.es_captura?"cap":"agg"].join("||");

  const caps = useMemo(()=>
    (datos?.capturas??[]).map(it=>({
      ...it, es_captura:true, _rk:rk(it), _m:calcM(it,mesesNum,pctMix)
    })),[datos,mesesNum,pctMix]);

  const hijosDe = ccpetPadre => {
    const pref = ccpetPadre+".";
    return caps.filter(c=>c._m&&(c.ccpet===ccpetPadre||c.ccpet?.startsWith(pref)));
  };

  const aggs = useMemo(()=>
    (datos?.agregados??[]).map(agg=>{
      const item={...agg,es_captura:false,_rk:rk(agg)};
      const hj=hijosDe(agg.ccpet);
      const rec={};
      mesesNum.forEach(m=>{rec[m]=hj.reduce((s,c)=>s+(Number(c.recaudos?.[m])||0),0);});
      item.recaudos=rec;
      const ultMes=mesesNum[mesesNum.length-1];
      item._m=hj.length>0?{
        auto:null,prom:null,mixt:null,mc:null,inc:null,
        ult:Math.round(hj.reduce((s,c)=>s+(Number(c.recaudos?.[ultMes])||0),0)),
        sumaMeses:Math.round(hj.reduce((s,c)=>s+(Number(c._m?.sumaMeses)||0),0)),
        tpPct:null
      }:null;
      return item;
    }),[datos,mesesNum,caps]);

  useEffect(()=>{
    if(!aggs.length) return;
    const init={};
    aggs.forEach(a=>{if(a.ccpet.split(".").length<=2) init[a.ccpet]=true;});
    setExpanded(init);
  },[datos]);

  const todas=useMemo(()=>[...aggs,...caps].sort((a,b)=>{
    const pa=a.ccpet.split(".").map(Number),pb=b.ccpet.split(".").map(Number);
    for(let i=0;i<Math.max(pa.length,pb.length);i++){const d=(pa[i]??0)-(pb[i]??0);if(d)return d;}
    return a.es_captura?1:-1;
  }),[aggs,caps]);

  const tieneHijos=it=>!it.es_captura&&todas.some(x=>x.ccpet!==it.ccpet&&x.ccpet.startsWith(it.ccpet+"."));

  const esVisible=it=>{
    const p=it.ccpet.split(".");
    if(p.length===1) return true;
    for(let i=1;i<p.length;i++){
      const anc=p.slice(0,i).join(".");
      if(todas.some(x=>x.ccpet===anc&&!x.es_captura)&&expanded[anc]===false) return false;
    }
    return true;
  };

  const getM=it=>it.es_captura?(mets[it._rk]??"auto"):"sum";
  const getV=it=>{
    if(!it.es_captura){
      return Math.round(hijosDe(it.ccpet).reduce((s,c)=>{
        const m=mets[c._rk]??"auto";
        return s+(m==="prop"?(parseFloat(propios[c._rk]?.valor)||0):(Number(c._m?.[m])||0));
      },0));
    }
    const m=mets[it._rk]??"auto";
    if(m==="prop") return parseFloat(propios[it._rk]?.valor)||0;
    return it._m?(it._m[m]??0):0;
  };
  const setM=(it,v)=>{if(it.es_captura) setMets(p=>({...p,[it._rk]:v}));};
  const setPV=(it,campo,val)=>savePropios({...propios,[it._rk]:{...propios[it._rk],[campo]:val}});

  const fltRes=todas.filter(it=>{
    const s=qR.toLowerCase();
    return(!s||it.ccpet.toLowerCase().includes(s)||it.nombre.toLowerCase().includes(s)||(it.cres??"").toLowerCase().includes(s))&&esVisible(it);
  });
  const fltHist=caps.filter(it=>{
    const s=qH.toLowerCase();
    return !s||it.ccpet.toLowerCase().includes(s)||it.nombre.toLowerCase().includes(s);
  });

  const hay=!loading&&caps.length>0;
  const nCargados=(datos?.meses??[]).length;
  const maxAlcanzado=nCargados>=11;

  /* estilos */
  const thH={padding:"0.5rem 0.65rem",background:"#163b8c",color:"#fff",
    fontWeight:700,fontSize:"0.72rem",textAlign:"left",whiteSpace:"nowrap"};
  const tdH=(i,e={})=>({padding:"0.4rem 0.65rem",fontSize:"0.75rem",
    background:i%2===0?"#f8faff":"#fff",borderBottom:"1px solid #e5e7eb",
    verticalAlign:"middle",...e});
  const card={background:"#fff",borderRadius:"16px",border:"1px solid #e5e7eb",
    boxShadow:"0 4px 14px rgba(0,0,0,0.05)",marginBottom:"1.5rem",overflow:"hidden"};

  return (
    <div style={{minHeight:"100vh",background:"#f3f4f6",padding:"2rem 1.5rem"}}>
      <div style={{maxWidth:"1500px",margin:"0 auto"}}>

        <button type="button" onClick={()=>navigate("/hacienda-presupuesto")}
          style={{background:"#e5e7eb",color:"#111827",border:"none",borderRadius:"10px",
            padding:"0.7rem 1rem",cursor:"pointer",fontWeight:600,marginBottom:"1.5rem"}}>
          ← Volver a Hacienda
        </button>

        <div style={{marginBottom:"1.5rem"}}>
          <h1 style={{margin:0,color:"#163b8c",fontSize:"2rem",fontWeight:800}}>
            Proyección de Ingresos — Vigencia Actual
          </h1>
          <p style={{marginTop:"0.5rem",color:"#6b7280"}}>
            Ejecuciones mensuales {vigencia} · Proyección anual por cuenta
            {nMeses>0&&<> · <strong>{nMeses}</strong> {nMeses===1?"mes":"meses"} cargados</>}
          </p>
        </div>

        {/* ── Meses cargados ── */}
        <div style={{...card,padding:"1.5rem"}}>
          <h2 style={{margin:"0 0 1rem",color:"#163b8c",fontSize:"1.1rem",fontWeight:700}}>
            Meses cargados — Vigencia {vigencia}
          </h2>

          {(datos?.meses??[]).length>0&&(
            <div style={{marginBottom:"1.2rem",borderRadius:"10px",overflow:"hidden",border:"1px solid #e5e7eb"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.88rem"}}>
                <thead><tr style={{background:"#163b8c"}}>
                  {["Mes","Archivo","Registros","Fecha cargue","Estado"].map(h=>(
                    <th key={h} style={{padding:"0.65rem 1rem",color:"#fff",fontWeight:700,
                      textAlign:["Registros","Estado"].includes(h)?"center":"left"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(datos?.meses??[]).map((m,i)=>(
                    <tr key={m.mes} style={{background:i%2===0?"#f0f6ff":"#fff",borderBottom:"1px solid #e5e7eb"}}>
                      <td style={{padding:"0.65rem 1rem",fontWeight:800,color:"#163b8c"}}>{m.nombre_mes}</td>
                      <td style={{padding:"0.65rem 1rem",color:"#6b7280",fontSize:"0.82rem",
                        maxWidth:"220px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {m.nombre_archivo??"—"}
                      </td>
                      <td style={{padding:"0.65rem 1rem",textAlign:"center",fontWeight:600}}>
                        {Number(m.total_registros).toLocaleString("es-CO")}
                      </td>
                      <td style={{padding:"0.65rem 1rem",color:"#6b7280",fontSize:"0.82rem"}}>
                        {new Date(m.fecha_cargue).toLocaleString("es-CO",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                      </td>
                      <td style={{padding:"0.65rem 1rem",textAlign:"center"}}>
                        <span style={{display:"inline-flex",alignItems:"center",gap:"0.3rem",
                          background:"#dcfce7",color:"#15803d",borderRadius:"999px",
                          padding:"0.25rem 0.75rem",fontWeight:700,fontSize:"0.78rem"}}>
                          <CheckCircle size={12}/> Cargado
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Selector de mes manual */}
          <div style={{display:"flex",alignItems:"center",gap:"0.75rem",flexWrap:"wrap",marginBottom:"0.85rem"}}>
            <select value={mesManual} onChange={e=>setMesManual(e.target.value)}
              style={{padding:"0.6rem 1rem",borderRadius:"8px",border:"1px solid #d1d5db",
                fontSize:"0.88rem",color:"#374151",outline:"none",background:"#fff"}}>
              <option value="">Detectar mes automáticamente del nombre del archivo</option>
              {Object.entries(MESES_NOMBRES).map(([k,v])=>(
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <input ref={fRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={handleUp}/>
            <button type="button" onClick={()=>!maxAlcanzado&&fRef.current.click()}
              disabled={subiendo||maxAlcanzado}
              style={{display:"flex",alignItems:"center",gap:"0.5rem",
                background:maxAlcanzado?"#9ca3af":subiendo?"#93c5fd":"#163b8c",
                color:"#fff",border:"none",borderRadius:"10px",padding:"0.75rem 1.3rem",
                fontWeight:700,cursor:(subiendo||maxAlcanzado)?"not-allowed":"pointer",
                boxShadow:"0 4px 12px rgba(22,59,140,0.2)"}}>
              <Upload size={18}/>
              {maxAlcanzado?"Máximo 11 meses alcanzado":subiendo?"Cargando...":"Cargar mes"}
            </button>
          </div>

          <p style={{margin:0,fontSize:"0.82rem",color:"#9ca3af"}}>
            CSV o Excel de ejecución mensual de ingresos. <strong>Máximo 11 meses</strong> (enero–noviembre).
            {maxAlcanzado&&<span style={{color:"#dc2626",fontWeight:600}}> Ya alcanzaste el máximo.</span>}
          </p>

          {nCargados>0&&nCargados<3&&(
            <div style={{marginTop:"0.75rem",padding:"0.75rem 1rem",borderRadius:"10px",
              background:"#fffbeb",border:"1px solid #fde68a",
              color:"#92400e",fontSize:"0.85rem",display:"flex",alignItems:"flex-start",gap:"0.5rem"}}>
              <span style={{fontSize:"1.1rem"}}>⚠️</span>
              <span>
                <strong>Tienes {nCargados} {nCargados===1?"mes":"meses"} cargado{nCargados===1?"":"s"}.</strong>{" "}
                Cargar menos de 3 meses puede no reflejar la realidad de los ingresos.
                Se recomienda tener al menos 3 meses para una proyección confiable.
              </span>
            </div>
          )}

          {msg&&(
            <div style={{marginTop:"1rem",padding:"0.85rem 1rem",borderRadius:"10px",
              background:msg.ok?"#f0fdf4":"#fef2f2",
              border:`1px solid ${msg.ok?"#bbf7d0":"#fca5a5"}`,
              color:msg.ok?"#15803d":"#991b1b",
              display:"flex",alignItems:"center",gap:"0.5rem",fontSize:"0.9rem"}}>
              {msg.ok?<CheckCircle size={16}/>:<AlertCircle size={16}/>}{msg.t}
            </div>
          )}
        </div>

        {hay&&(<>

          {/* ── Histórico mensual ── */}
          <Bloque titulo="Recaudo mensual por cuenta"
            badge={`${caps.length} cuentas`}
            sub={`Meses: ${mesesNum.map(m=>MESES_NOMBRES[m]).join(", ")}`}
            open={open.hist} onToggle={()=>tog("hist")}>
            <input type="text" placeholder="Buscar CCPET o nombre..." value={qH}
              onChange={e=>setQH(e.target.value)}
              style={{padding:"0.55rem 1rem",borderRadius:"8px",border:"1px solid #d1d5db",
                fontSize:"0.88rem",maxWidth:"300px",outline:"none",marginBottom:"1rem",display:"block"}}/>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  <th style={thH}>CCPET</th><th style={thH}>C.RES</th>
                  <th style={{...thH,minWidth:"200px"}}>NOMBRE</th>
                  {mesesNum.map(m=><th key={m} style={{...thH,textAlign:"right"}}>{MESES_NOMBRES[m]}</th>)}
                  <th style={{...thH,textAlign:"right",background:"#15803d",minWidth:"140px"}}>Proy. Anual {vigencia}</th>
                </tr></thead>
                <tbody>
                  {fltHist.map((it,i)=>(
                    <tr key={it._rk}>
                      <td style={tdH(i)}><code style={{fontSize:"0.68rem"}}>{it.ccpet}</code></td>
                      <td style={tdH(i,{fontSize:"0.7rem",color:"#6b7280"})}>{it.cres??"—"}</td>
                      <td style={tdH(i,{maxWidth:"240px"})}>{it.nombre}</td>
                      {mesesNum.map(m=>(
                        <td key={m} style={{...tdH(i),textAlign:"right",fontFamily:"monospace",fontSize:"0.72rem"}}>
                          {(it.recaudos?.[m]||0)>0?fCOP(it.recaudos[m]):<span style={{color:"#d1d5db"}}>—</span>}
                        </td>
                      ))}
                      <td style={{...tdH(i),textAlign:"right",fontFamily:"monospace",fontSize:"0.76rem",
                        fontWeight:700,color:"#065f46",background:i%2===0?"#dcfce7":"#bbf7d0"}}>
                        {fCOP(getV(it))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Bloque>

          {/* ── Vista Liquidación mensual ── */}
          <Bloque titulo="Vista Liquidación — Proyección anual consolidada"
            badge={`${aggs.length} niveles`}
            sub={`Suma de cuentas según método · Vigencia ${vigencia}`}
            open={open.liq} onToggle={()=>tog("liq")}>
            <p style={{fontSize:"0.8rem",color:"#6b7280",margin:"0 0 1rem"}}>
              Tabla jerárquica. Los valores de proyección reflejan el método seleccionado en el Resumen comparativo.
            </p>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.78rem"}}>
                <thead><tr>
                  <th style={thH}>CCPET</th>
                  <th style={{...thH,minWidth:"260px"}}>NOMBRE</th>
                  {mesesNum.map(m=><th key={m} style={{...thH,textAlign:"right",fontSize:"0.68rem"}}>{MESES_NOMBRES[m].slice(0,3)}.</th>)}
                  <th style={{...thH,textAlign:"right",background:"#15803d"}}>Proy. Anual {vigencia}</th>
                </tr></thead>
                <tbody>
                  {aggs.map((agg,i)=>{
                    const nivel=(agg.ccpet.match(/\./g)||[]).length;
                    const indent=nivel*14;
                    const peso=nivel===0?800:nivel<=1?700:nivel<=3?600:400;
                    const bgRow=nivel===0?"#1e3a8a":nivel===1?"#1e40af":
                                nivel===2?"#2563eb":nivel===3?"#dbeafe":
                                i%2===0?"#f0f6ff":"#fff";
                    const clr=nivel<=2?"#fff":"#111827";
                    const proy=getV(agg);
                    return (
                      <tr key={agg.ccpet}>
                        <td style={{padding:"0.45rem 0.65rem",background:bgRow,
                          borderBottom:"1px solid #e5e7eb",fontFamily:"monospace",
                          fontSize:"0.72rem",color:nivel<=2?"#93c5fd":nivel<=3?"#1e40af":"#374151",fontWeight:peso}}>
                          {agg.ccpet}
                        </td>
                        <td style={{padding:`0.45rem 0.65rem 0.45rem ${indent+8}px`,
                          background:bgRow,borderBottom:"1px solid #e5e7eb",
                          fontSize:"0.75rem",fontWeight:peso,color:clr,maxWidth:"320px"}}>
                          {agg.nombre}
                        </td>
                        {mesesNum.map(m=>(
                          <td key={m} style={{padding:"0.45rem 0.65rem",background:bgRow,
                            borderBottom:"1px solid #e5e7eb",textAlign:"right",
                            fontFamily:"monospace",fontSize:"0.72rem",
                            color:nivel<=2?"#bfdbfe":nivel<=3?"#1e40af":"#374151",fontWeight:peso}}>
                            {(agg.recaudos?.[m]||0)>0?fCOP(agg.recaudos[m]):"—"}
                          </td>
                        ))}
                        <td style={{padding:"0.45rem 0.65rem",background:nivel<=2?"#065f46":"#dcfce7",
                          borderBottom:"1px solid #e5e7eb",textAlign:"right",
                          fontFamily:"monospace",fontSize:"0.76rem",
                          fontWeight:700,color:nivel<=2?"#a7f3d0":"#065f46"}}>
                          {proy>0?fCOP(proy):"—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Bloque>

          {/* ── Resumen comparativo mensual ── */}
          <Bloque titulo="Proyección de Ingresos"
            badge={`${todas.length} filas`}
            sub={`Proyección anual ${vigencia}`}
            open={open.res} onToggle={()=>tog("res")}>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              flexWrap:"wrap",gap:"0.75rem",marginBottom:"0.75rem"}}>
              <div style={{display:"flex",alignItems:"center",gap:"0.75rem",flexWrap:"wrap"}}>
                <input type="text" placeholder="Buscar CCPET, nombre..." value={qR}
                  onChange={e=>setQR(e.target.value)}
                  style={{padding:"0.55rem 1rem",borderRadius:"8px",border:"1px solid #d1d5db",
                    fontSize:"0.88rem",minWidth:"240px",outline:"none"}}/>
                <div style={{display:"inline-flex",alignItems:"center",gap:"0.4rem",
                  background:"#fff",border:"1px solid #d1d5db",borderRadius:"8px",padding:"0.25rem 0.6rem"}}>
                  <span style={{color:"#6b7280",fontSize:"0.82rem"}}>Ajuste (c):</span>
                  <input type="number" value={pctMix} onChange={e=>setPctMix(parseFloat(e.target.value)||0)}
                    style={{width:"55px",border:"none",outline:"none",fontSize:"0.88rem",
                      fontWeight:700,color:"#163b8c",textAlign:"right"}}/>
                  <span style={{color:"#6b7280",fontSize:"0.82rem"}}>%</span>
                </div>
              </div>
              <button type="button"
                onClick={()=>descargar(fltRes.map(it=>({
                  ccpet:it.ccpet,nombre:it.nombre,
                  fuente:it.fuente_codigo??"",cres:it.cres??"",
                  esAgg:!it.es_captura,
                  met:it.es_captura?(ML[getM(it)]??getM(it)):"Sumatoria",
                  val:getV(it), obs:propios[it._rk]?.obs??"",
                })),vigencia)}
                style={{display:"flex",alignItems:"center",gap:"0.5rem",
                  background:"#163b8c",color:"#fff",border:"none",borderRadius:"10px",
                  padding:"0.6rem 1.2rem",fontWeight:700,fontSize:"0.88rem",cursor:"pointer"}}>
                <Download size={16}/> Descargar proyección {vigencia}
              </button>
            </div>

            <p style={{fontSize:"0.79rem",color:"#6b7280",margin:"0 0 0.75rem"}}>
              Las proyecciones son <strong>anuales</strong> (prom. mensual × 12 meses restantes).
              <strong style={{color:"#1e3a8a"}}> Azul oscuro</strong> = agregados. Usa <strong>f. Propio</strong> para valor manual.
            </p>

            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.71rem"}}>
                <thead><tr>
                  <th style={{...thH,maxWidth:"110px"}}>CCPET</th>
                  <th style={thH}>C.RES</th>
                  <th style={{...thH,minWidth:"160px"}}>NOMBRE</th>
                  <th style={{...thH,textAlign:"right"}}>Suma meses</th>
                  <th style={{...thH,textAlign:"right"}}>a. Auto.</th>
                  <th style={{...thH,textAlign:"right"}}>b. Prom.×12</th>
                  <th style={{...thH,textAlign:"right"}}>c. Mixto</th>
                  <th style={{...thH,textAlign:"right"}}>d. Mín.C.</th>
                  <th style={{...thH,textAlign:"right"}}>e. Incr.</th>
                  <th style={{...thH,textAlign:"center",background:"#1e40af"}}>Método</th>
                  <th style={{...thH,textAlign:"right",background:"#15803d"}}>Proy. Anual</th>
                  <th style={{...thH,minWidth:"150px",background:"#374151"}}>Observaciones</th>
                </tr></thead>
                <tbody>
                  {fltRes.map((it,i)=>{
                    const esA=!it.es_captura,m=it._m;
                    const bg=esA?(i%2===0?"#dbeafe":"#bfdbfe"):(i%2===0?"#f8faff":"#fff");
                    const ct=esA?"#1e3a8a":"#111827",fw=esA?700:400;
                    const ms=getM(it),vs=getV(it);
                    const isProp=ms==="prop";
                    const propVal=propios[it._rk]?.valor??"";
                    const propObs=propios[it._rk]?.obs??"";
                    const tb=(e={})=>({padding:"0.35rem 0.6rem",fontSize:"0.71rem",
                      background:bg,borderBottom:"1px solid #e5e7eb",
                      verticalAlign:"middle",color:ct,fontWeight:fw,...e});
                    const tr=(e={})=>tb({textAlign:"right",fontFamily:"monospace",...e});
                    return (
                      <tr key={it._rk}>
                        <td style={tb({maxWidth:"110px"})}>
                          <div style={{display:"flex",alignItems:"center",gap:"0.3rem"}}>
                            {!it.es_captura&&tieneHijos(it)?(
                              <button type="button"
                                onClick={()=>setExpanded(p=>({...p,[it.ccpet]:!p[it.ccpet]}))}
                                style={{border:"none",background:"transparent",cursor:"pointer",
                                  padding:0,display:"flex",alignItems:"center",color:"#1e40af"}}>
                                {expanded[it.ccpet]?<ChevronDown size={13}/>:<ChevronRight size={13}/>}
                              </button>
                            ):<span style={{width:13,display:"inline-block"}}/>}
                            <code style={{fontSize:"0.67rem",color:esA?"#1e40af":"#374151"}}>{it.ccpet}</code>
                          </div>
                        </td>
                        <td style={tb({fontSize:"0.68rem",color:"#6b7280"})}>{it.cres??""}</td>
                        <td style={tb({maxWidth:"200px"})}>{it.nombre}</td>
                        <td style={tr()}>{m?fCOP(m.sumaMeses):"—"}</td>
                        {["auto","prom","mixt","mc","inc"].map(k=>(
                          <td key={k} style={tr({
                            background:ms===k?(i%2===0?"#bbf7d0":"#a7f3d0"):bg,
                            color:ms===k?"#065f46":ct,fontWeight:ms===k?700:fw,
                          })}>
                            {it.es_captura?(m?fCOP(m[k]):"—"):"—"}
                          </td>
                        ))}
                        <td style={tb({textAlign:"center",background:i%2===0?"#eff6ff":"#dbeafe"})}>
                          {it.es_captura?(
                            <select value={ms} onChange={e=>setM(it,e.target.value)}
                              style={{fontSize:"0.7rem",border:"1px solid #93c5fd",borderRadius:"6px",
                                padding:"0.2rem 0.3rem",background:"#fff",cursor:"pointer",
                                color:"#1e40af",fontWeight:600,outline:"none"}}>
                              <option value="auto">a. Automático</option>
                              <option value="prom">b. Prom.×12</option>
                              <option value="mixt">c. Mixto</option>
                              <option value="mc">d. Mín. Cuadrados</option>
                              <option value="inc">e. Incr. Porcentual</option>
                              <option value="prop">f. Propio ✏️</option>
                            </select>
                          ):<span style={{color:"#1e3a8a",fontWeight:700,fontSize:"0.7rem"}}>Sumatoria</span>}
                        </td>
                        <td style={tr({background:i%2===0?"#dcfce7":"#bbf7d0",color:"#065f46",fontWeight:700})}>
                          {it.es_captura&&isProp?(
                            <input type="number" placeholder="Valor anual"
                              value={propVal} onChange={e=>setPV(it,"valor",e.target.value)}
                              style={{width:"110px",border:"1px solid #6ee7b7",borderRadius:"6px",
                                padding:"0.2rem 0.4rem",fontSize:"0.71rem",textAlign:"right",
                                color:"#065f46",fontWeight:700,outline:"none",background:"#fff"}}/>
                          ):fCOP(vs)}
                        </td>
                        <td style={tb({background:isProp&&it.es_captura?(i%2===0?"#fffbeb":"#fef9c3"):bg})}>
                          {it.es_captura?(
                            <input type="text"
                              placeholder={isProp?"Ej: Convenio departamento":"Observación..."}
                              value={propObs} onChange={e=>setPV(it,"obs",e.target.value)}
                              style={{width:"100%",minWidth:"130px",border:`1px solid ${isProp?"#fbbf24":"#e5e7eb"}`,
                                borderRadius:"6px",padding:"0.2rem 0.4rem",fontSize:"0.7rem",
                                color:"#374151",outline:"none",background:isProp?"#fffbeb":"transparent"}}/>
                          ):<span style={{color:"#6b7280",fontSize:"0.69rem"}}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Bloque>

        </>)}

        {loading&&<div style={{textAlign:"center",color:"#6b7280",padding:"3rem"}}>Cargando datos...</div>}
        {!loading&&caps.length===0&&(datos?.meses??[]).length===0&&(
          <div style={{background:"#fff",borderRadius:"16px",border:"1px solid #e5e7eb",
            padding:"3rem",textAlign:"center",color:"#6b7280"}}>
            Carga al menos 3 meses de ejecución para comenzar.
          </div>
        )}
      </div>
    </div>
  );
}